import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from "react";
import {
  applyComfortableSessionToDocument,
  applyReduceMotionToDocument,
  readStoredComfortableSession,
  readStoredReduceMotion,
  writeStoredComfortableSession,
  writeStoredReduceMotion,
} from "@/lib/studyComfort";
import { normalizeBackendUrl } from "@/lib/api";

const BACKEND_URL = normalizeBackendUrl(import.meta.env.VITE_BACKEND_URL as string | undefined);
const TOKEN_KEY = "token";

interface StudyComfortContextValue {
  reduceMotion: boolean;
  comfortableSession: boolean;
  setReduceMotion: (enabled: boolean, options?: { persistRemote?: boolean }) => void;
  setComfortableSession: (enabled: boolean, options?: { persistRemote?: boolean }) => void;
}

const StudyComfortContext = createContext<StudyComfortContextValue>({
  reduceMotion: false,
  comfortableSession: false,
  setReduceMotion: () => {},
  setComfortableSession: () => {},
});

export function StudyComfortProvider({ children }: { children: React.ReactNode }) {
  const [reduceMotion, setReduceMotionState] = useState(() => readStoredReduceMotion());
  const [comfortableSession, setComfortableSessionState] = useState(() => readStoredComfortableSession());

  useLayoutEffect(() => {
    applyReduceMotionToDocument(reduceMotion);
    writeStoredReduceMotion(reduceMotion);
  }, [reduceMotion]);

  useLayoutEffect(() => {
    applyComfortableSessionToDocument(comfortableSession);
    writeStoredComfortableSession(comfortableSession);
  }, [comfortableSession]);

  const persistPrefs = useCallback((patch: { reduceMotion?: boolean; comfortableSession?: boolean }) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    fetch(`${BACKEND_URL}/api/user/preferences`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...(patch.reduceMotion !== undefined ? { reduceMotion: patch.reduceMotion } : {}),
        ...(patch.comfortableSession !== undefined ? { comfortableSession: patch.comfortableSession } : {}),
      }),
    }).catch(() => {});
  }, []);

  const setReduceMotion = useCallback((enabled: boolean, options?: { persistRemote?: boolean }) => {
    setReduceMotionState(enabled);
    if (options?.persistRemote === false) return;
    persistPrefs({ reduceMotion: enabled });
  }, [persistPrefs]);

  const setComfortableSession = useCallback((enabled: boolean, options?: { persistRemote?: boolean }) => {
    setComfortableSessionState(enabled);
    if (options?.persistRemote === false) return;
    persistPrefs({ comfortableSession: enabled });
  }, [persistPrefs]);

  const value = useMemo(
    () => ({ reduceMotion, comfortableSession, setReduceMotion, setComfortableSession }),
    [reduceMotion, comfortableSession, setReduceMotion, setComfortableSession],
  );

  return (
    <StudyComfortContext.Provider value={value}>
      {children}
    </StudyComfortContext.Provider>
  );
}

export function useStudyComfort() {
  return useContext(StudyComfortContext);
}
